import { Capacitor } from '@capacitor/core';
import { Box, Slide, useTheme } from '@mui/material';
import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useLayoutEffect,
  useRef,
} from 'react';

import { ConditionalWrap } from '@frontend-libs/atomic';
import { PortalTheme } from '@frontend-libs/portal';

import { getActionBarBottomOffset } from './app-bottom-actionbar-offset';
import { useOptionalBottomNavbar } from './app-bottom-navbar-provider.component';

export interface AppBottomActionBarProps {
  slideEnabled?: boolean;
  slideCondition?: boolean;
  bottomOffset?: string;
  /**
   * Reports the bar's rendered height (in px) whenever it changes, so callers
   * can reserve exactly enough clearance for it (e.g. via `View`'s
   * `bottomNavbarPadding`) instead of assuming a fixed height. Called with
   * `0` on unmount.
   */
  onHeightChange?: (height: number) => void;
}

export const AppBottomActionBar = (
  props: PropsWithChildren<AppBottomActionBarProps>,
) => {
  const theme = useTheme<PortalTheme>();
  const isNavbarVisible = useOptionalBottomNavbar()?.isVisible ?? false;
  const boxRef = useRef<HTMLDivElement>(null);
  const { onHeightChange } = props;
  const { bottom, paddingBottom } = getActionBarBottomOffset({
    bottomOffset: props.bottomOffset,
    isNavbarVisible,
    isNative: Capacitor.isNativePlatform(),
    bottomNavbarHeight: theme.bottomNavbarHeight,
  });

  useLayoutEffect(() => {
    const node = boxRef.current;

    if (!node || !onHeightChange || typeof ResizeObserver === 'undefined') {
      return;
    }

    const reportHeight = () =>
      onHeightChange(node.getBoundingClientRect().height);
    reportHeight();

    const observer = new ResizeObserver(reportHeight);
    observer.observe(node);

    return () => {
      observer.disconnect();
      onHeightChange(0);
    };
  }, [onHeightChange]);

  return (
    <ConditionalWrap
      condition={props.slideEnabled ?? false}
      wrap={(children) => (
        <Slide
          in={props.slideCondition !== undefined ? props.slideCondition : true}
          direction="up"
        >
          {children as ReactElement<ReactNode>}
        </Slide>
      )}
    >
      <Box
        ref={boxRef}
        sx={{
          width: '100%',
          bottom,
          left: 0,
          right: 0,
          position: 'absolute',
          backgroundColor: theme.palette.background.paper,
          zIndex: theme.zIndex.appBar,
          pb: paddingBottom,
        }}
        display="flex"
        justifyContent="center"
      >
        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            p: 2,
            pb: 3,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              // This box is extra to constrain the max width of the content
              // so it looks better on larger screens but at the same time let the
              // divider fill the entire bottom screen.
              width: '100%',
              [theme.breakpoints.up('sm')]: {
                maxWidth: theme.contentMaxWidth,
              },
            }}
          >
            {props.children}
          </Box>
        </Box>
      </Box>
    </ConditionalWrap>
  );
};
